import { AlertCircle, AlertTriangle, Clock, FolderOpen, HandCoins, Scale } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { IncidentAmountByCurrency, PaymentIncidentsSummary } from "../types/paymentIncidents.types";

export interface IncidentsSummaryStripProps {
  summary: PaymentIncidentsSummary | null;
  isLoading: boolean;
  /** The last read failed: with a `summary`, its figures are out of date. */
  error?: string | null;
  onRetry?: () => void;
  /** "Open" pressed: the list shows only movements whose incident still waits on someone. */
  openSelected: boolean;
  onSelectOpen: () => void;
  masked?: boolean;
}

/**
 * The claims at a glance: how many are open, how many have had no news for a week, what they put at stake and
 * what Coloca already refunded to clients without the provider returning it yet.
 */
export function IncidentsSummaryStrip({
  summary,
  isLoading,
  error = null,
  onRetry,
  openSelected,
  onSelectOpen,
  masked = false,
}: IncidentsSummaryStripProps) {
  const { t, activeLanguage } = useTranslations("paymentIncidents");
  const retry = onRetry ? (
    <ColocaButton variant="secondary" size="sm" loading={isLoading} disabled={isLoading} onClick={onRetry}>
      {t("action_retry")}
    </ColocaButton>
  ) : undefined;
  if (error && !summary) {
    return <ColocaAlert variant="destructive" icon={AlertCircle} text={t("summary_error")} actions={retry} />;
  }
  if (isLoading && !summary) {
    return (
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4" aria-busy="true">
        {Array.from({ length: 4 }, (_, index) => (
          <ColocaSkeleton key={index} className="h-[88px] w-full rounded-xl" />
        ))}
      </div>
    );
  }
  if (!summary) return null;

  const amounts = (rows: IncidentAmountByCurrency[]) =>
    rows.length ? (
      <span className="flex flex-col gap-0.5">
        {rows.map((row) => (
          <ColocaAmountDisplay
            key={row.currency}
            value={row.amount}
            currency={row.currency}
            fractionDigits={Math.min(minorUnitsFor(row.currency), 2)}
            locale={activeLanguage}
            size="sm"
            masked={masked}
          />
        ))}
      </span>
    ) : (
      "—"
    );

  // Figures that could not be read again are still money on screen: they say they are old, and since when.
  const outdated = !!error;
  const cards = (
    <div
      className={cn("grid grid-cols-2 gap-3 xl:grid-cols-4", outdated && "opacity-60")}
      aria-describedby={outdated ? "incidents-summary-outdated" : undefined}
    >
      <ColocaKpiCard
        icon={FolderOpen}
        label={t("summary_open")}
        value={summary.open}
        meta={t("summary_open_meta", {
          pending: summary.by_status.pending_report,
          waiting: summary.by_status.reported + summary.by_status.in_progress,
        })}
        selected={openSelected}
        onSelect={onSelectOpen}
      />
      <ColocaKpiCard
        icon={Clock}
        label={t("summary_stale")}
        value={summary.stale}
        meta={t("summary_stale_meta")}
      />
      <ColocaKpiCard icon={Scale} label={t("summary_at_stake")} value={amounts(summary.open_at_stake)} />
      <ColocaKpiCard
        icon={HandCoins}
        label={t("summary_advanced")}
        value={amounts(summary.advanced_by_coloca)}
        meta={t("summary_advanced_meta")}
      />
    </div>
  );
  if (!outdated) return cards;
  return (
    <div className="space-y-3">
      <ColocaAlert
        id="incidents-summary-outdated"
        variant="warning"
        icon={AlertTriangle}
        text={t("summary_outdated", {
          time: formatDateTime(summary.generated_at, { locale: activeLanguage, includeTime: true }),
        })}
        actions={retry}
      />
      {cards}
    </div>
  );
}
